import {platformAvailable} from '../lib/platformTools.js'
import {useAccess} from '../lib/access.jsx'
import {useToolAccess,PlatformLink} from '../lib/toolAccess.jsx'
import { useMemo } from 'react'
import { useAuth } from '../lib/auth.jsx'
import { NAV, systemDirectory } from '../nav.js'
import { INTERNAL_ROUTE, INTERNAL_LABEL } from '../lib/surfaces.js'
import PageHeader from '../components/PageHeader.jsx'
import OutreachScoreboard from '../features/integrity/OutreachScoreboard.jsx'
import OutreachCoveragePanel from '../features/integrity/OutreachCoveragePanel.jsx'
import { IconArrow } from '../components/icons.jsx'
import { useChampionships } from '../features/command-center/useChampionships.js'
import JourneyCoverageNotice from '../features/customer-journey/JourneyCoverageNotice.jsx'
import { useAccounts } from '../features/customer-journey/useAccounts.js'
import { inWindow, sortByDate, formatDate, countByState } from '../lib/championships.js'
import { NATIONAL } from '../lib/usStates.js'
import { formatRelative } from '../lib/clickup.js'
import { orderRollup } from '../lib/orderRollup.js'
import { PHASE_LABEL, PHASE_TONE } from '../lib/journey.js'
import { BUILD, buildDetail, displayVersion } from '../lib/buildInfo.js'
import styles from './Home.module.css'

/**
 * ⚠️ THE DESCRIPTIONS COME FROM nav.js, NOT FROM A MAP IN HERE.
 *
 * There WAS a map in here, and it had already drifted: `/reviews`,
 * `/team-rooms` and `/top-tier-teams` were all missing from it, so three
 * modules rendered with `undefined` under their titles. That is the exact
 * failure a second list produces — the page that explains the system becomes
 * the thing that is wrong about it.
 *
 * One registry now. A module added to the sidebar arrives here described, and
 * one added without a description says so on screen instead of rendering blank.
 */

function firstName(user) {
  const n = user?.name || user?.username || ''
  return n.split(/[\s@._-]+/)[0] || 'there'
}

export default function Home() {
  const { user } = useAuth()
  const tools=useToolAccess(),{visibleNav}=useAccess()
  const modules = visibleNav(NAV).filter((n) => n.to !== '/'&&platformAvailable(tools,n.to)&&(n.to!=='/artwork-studio'||tools?.available('artwork')))
  /* One registry, read once. See the note above MODULE_DESC's grave. */
  const directory = systemDirectory(modules)

  return (
    <div className="fade-up">
      <PageHeader
        eyebrow="The Platform"
        title={`Welcome back, ${firstName(user)}.`}
        lead="Coordinate championships, customer relationships, artwork, pricing and delivery in one Southern Recognition workspace."
      />

      <section className={styles.hero}>
        <div className={styles.heroCard}>
          <div className={styles.heroEyebrow}>Artwork Studio</div>
          <h2 className={styles.heroTitle}>Design against the real brand kit.</h2>
          <p className={styles.heroLead}>
            Search a school to check its owned logo and saved colors in HubSpot,
            then prepare the artwork against that brand record.
          </p>
          <div className={styles.heroActions}>
            <PlatformLink className="btn btn-primary" to="/artwork-studio">
              Open Artwork Studio <IconArrow width={16} height={16} />
            </PlatformLink>
            <PlatformLink
              className="btn"
              to="/command-center"
              style={{ background: 'transparent', color: '#f4efe3', borderColor: 'rgba(244,239,227,0.25)' }}
            >
              Command Center
            </PlatformLink>
          </div>
        </div>

        <div className={styles.stats}>
          <div className={`card ${styles.stat}`}>
            {/* Derived from the same list the tiles render, not hardcoded, so
                adding a module can never leave this count stale. */}
            <span className={styles.statNum}>{modules.length}</span>
            <span className={styles.statLabel}>Platform modules</span>
            <span className={styles.statTick}>Unified</span>
          </div>
          <div className={`card ${styles.stat}`}>
            <span className={styles.statNum}>CRM</span>
            <span className={styles.statLabel}>Customer relationships · HubSpot</span>
            <span className={styles.statTick}>Customer records</span>
          </div>
          <div className={`card ${styles.stat}`}>
            <span className={styles.statNum} title={buildDetail()}>{displayVersion()}</span>
            <span className={styles.statLabel}>Release · versioned &amp; deployable</span>
            <span className={styles.statTick}>{BUILD.commit}{BUILD.dirty ? '+local' : ''}</span>
          </div>
        </div>
      </section>

      <LiveRollup />
      <OutreachCoveragePanel />

      <div className="eyebrow" style={{ marginTop: 8 }}>
        What each system is for
      </div>
      {/* ⚠️ THE SCOREBOARD SITS ON THE FRONT DOOR ON PURPOSE.
          Brian: "I want to make sure I have visibility on it so we can measure
          it and manage it. If we see our team has higher close rate with video,
          I want it pushed harder."

          A comparison nobody looks at cannot change behaviour, and a report
          that lives three clicks into a module is a report nobody looks at.
          It degrades on its own — a failed read renders its own honest card
          rather than zeros, so it can never make Home look broken or make the
          team look idle. */}
      <section className={styles.outreach}>
        <OutreachScoreboard />
      </section>

      <p className={styles.dirLead}>
        Ten surfaces, and two of them show the same production stages on purpose.
        The line that tells them apart is <strong>who is looking</strong> — SR at every
        account, or one customer at their own.
      </p>
      <div className={styles.modules}>
        {modules.map(({ to, label, icon: Icon, status }) => {
          const meta = directory.find((d) => d.to === to)
          return (
            <PlatformLink key={to} to={to} className={`card ${styles.module}`}>
              <div className={styles.moduleIcon}>
                <Icon width={20} height={20} />
              </div>
              <div className={styles.moduleTitle}>
                {label}
                {/* Internal or customer-visible — Brian's boundary, and the one
                    field that separates Production from Team Meeting Rooms. It
                    sits on the title line because it is what a rep needs before
                    they pick, not after. */}
                <span
                  className={`${styles.who} ${
                    meta?.audience === 'Customer' ? styles.whoCustomer : styles.whoSR
                  }`}
                >
                  {meta?.audience === 'Customer' ? 'Customer signs in' : 'SR only'}
                </span>
              </div>
              <p className={styles.moduleDesc}>{meta?.purpose}</p>
              {meta?.reads ? (
                <p className={styles.moduleReads}>
                  <span>Reads</span> {meta.reads}
                </p>
              ) : null}
              <div className={styles.moduleFoot}>
                {status === 'live' ? 'Open' : 'Preview'}{' '}
                <IconArrow width={15} height={15} />
              </div>
            </PlatformLink>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Live operational rollup that ties the modules together: real KPIs and
 * worklists read from the same feeds the Command Center and Production
 * use. Each feed degrades independently — a feed that's loading or errored
 * shows a soft state, never breaking the landing page.
 */
function LiveRollup() {
  const champs = useChampionships()
  const accounts = useAccounts()
  const now = useMemo(() => new Date(), [])

  const champStats = useMemo(() => {
    const data = champs.data
    const counts = countByState(data)
    return {
      total: data.length,
      next30: data.filter((c) => inWindow(c, 'next30', now)).length,
      next90: data.filter((c) => inWindow(c, 'next90', now)).length,
      states: Object.keys(counts).filter((k) => k !== NATIONAL).length,
      upcoming: sortByDate(
        data.filter((c) => inWindow(c, 'next90', now)),
        true
      ).slice(0, 5),
    }
  }, [champs.data, now])

  const orderStats = useMemo(() => orderRollup(accounts.data, now), [accounts.data, now])

  const cVal = (v) =>
    champs.status === 'loading' ? '—' : champs.status === 'error' ? '—' : v
  const oVal = (v) =>
    accounts.status === 'done' && accounts.coverage?.fullOrderBook ? v : 'Unmeasured'

  return (
    <>
      <div className={styles.rollupHead}>
        <div className="eyebrow">At a glance</div>
        <span className={styles.rollupLive}>
          <span className={styles.rollupDot} /> Source reads · coverage below
        </span>
      </div>

      <div className={styles.kpis}>
        <KpiCard to="/command-center" value={cVal(champStats.total)} label="Championships" sub="tracked" />
        <KpiCard to="/command-center" value={cVal(champStats.next30)} label="Next 30 days" sub="upcoming" tone="brass" />
        <KpiCard to="/command-center" value={cVal(champStats.states)} label="States" sub="represented" />
        <KpiCard to={INTERNAL_ROUTE} value={oVal(orderStats.total)} label="Active orders" sub="in flight" />
        <KpiCard to={INTERNAL_ROUTE} value={oVal(orderStats.approval)} label="Awaiting approval" sub="need sign-off" tone="warn" />
        <KpiCard to={INTERNAL_ROUTE} value={oVal(orderStats.attention)} label="Needs attention" sub="overdue / hold" tone="danger" />
      </div>

      <JourneyCoverageNotice feed={accounts} />
      <div className={styles.worklists}>
        {/* Upcoming championships */}
        <div className={`card ${styles.worklist}`}>
          <div className={styles.worklistHead}>
            <div className={styles.worklistTitle}>Upcoming championships</div>
            <PlatformLink to="/command-center" className={styles.worklistLink}>
              Command Center <IconArrow width={14} height={14} />
            </PlatformLink>
          </div>
          <FeedBody
            status={champs.status}
            empty={!champStats.upcoming.length}
            emptyText="No championships in the next 90 days."
          >
            {/* ⚠️ THE ROW OPENS THE RECORD. These were inert <div>s — a
                worklist of championships a rep should act on, and tapping one
                did nothing. The card header linked to the module, which is the
                same as not linking to the record. */}
            {champStats.upcoming.map((c) => (
              <PlatformLink key={c.id} to={`/command-center?record=${encodeURIComponent(c.id)}`} className={styles.item}>
                <span className={styles.itemDate}>{formatDate(c.dateObj)}</span>
                <span className={styles.itemName}>{c.name}</span>
                <span className={styles.itemTag}>
                  {c.state === NATIONAL ? 'National' : c.state}
                </span>
              </PlatformLink>
            ))}
          </FeedBody>
        </div>

        {/* Needs attention */}
        <div className={`card ${styles.worklist}`}>
          <div className={styles.worklistHead}>
            <div className={styles.worklistTitle}>Needs attention</div>
            <PlatformLink to={INTERNAL_ROUTE} className={styles.worklistLink}>
              {INTERNAL_LABEL} <IconArrow width={14} height={14} />
            </PlatformLink>
          </div>
          {/* 🔴 THE COUNT RECONCILES, OUT LOUD. Brian: "If 12 becomes 3, show
            * that 9 were excluded as out-of-season with a way to see them."
            * A number that silently shrank is how a real problem disappears
            * unnoticed — and an order open since 2023 IS a real problem, just
            * not a morning-screen one. */}
          {orderStats.staleCount > 0 && (
            <p className={styles.scopeNote}>
              {orderStats.staleLine}{' '}
              <PlatformLink to={INTERNAL_ROUTE} className={styles.scopeLink}>
                See them in {INTERNAL_LABEL}
              </PlatformLink>
            </p>
          )}
          <FeedBody
            status={accounts.status}
            empty={!orderStats.list.length}
            emptyText={!accounts.coverage?.fullOrderBook ? 'No attention items in the returned subset. Full-book coverage is not established.' : orderStats.staleCount > 0
              /* ⚠️ AN EMPTY CARD AFTER A PARTITION MUST NOT READ AS A CLEAR
               * DESK. If everything that was here got held back as stale, the
               * honest sentence says so rather than congratulating anybody. */
              ? 'Nothing from this season needs attention — everything outstanding is older.'
              : 'Nothing needs attention right now.'}
          >
            {orderStats.list.map((a) => {
              const overdue = a.atRisk
              return (
                // Same fix. The journey has no per-record route yet, so this
                // lands on the module rather than pretending to deep-link.
                <PlatformLink key={a.id} to={INTERNAL_ROUTE} className={styles.item}>
                  <span
                    className={`${styles.itemDate} ${overdue ? styles.itemOverdue : ''}`}
                  >
                    {a.dueDate ? formatRelative(a.dueDate, now) : '—'}
                  </span>
                  <span className={styles.itemName}>
                    {a.org || 'Account'}
                    <span className={styles.itemSub}>{a.orderNo}</span>
                  </span>
                  <span className={styles.itemTag} data-tone={PHASE_TONE[a.phase]}>
                    {PHASE_LABEL[a.phase] || a.phase}
                  </span>
                </PlatformLink>
              )
            })}
          </FeedBody>
        </div>
      </div>
    </>
  )
}

function KpiCard({ to, value, label, sub, tone }) {
  return (
    <PlatformLink to={to} className={`card ${styles.kpi}`} data-tone={tone || ''}>
      <span className={styles.kpiNum}>{value}</span>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={styles.kpiSub}>{sub}</span>
    </PlatformLink>
  )
}

function FeedBody({ status, empty, emptyText, children }) {
  if (status === 'loading') {
    return (
      <div className={styles.feedSkel}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className={styles.skelLine} />
        ))}
      </div>
    )
  }
  if (status === 'error' || status === 'unavailable') {
    return <div className={styles.feedNote}>Feed unavailable right now.</div>
  }
  if (empty) return <div className={styles.feedNote}>{emptyText}</div>
  return <div className={styles.items}>{children}</div>
}
